import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardDisplay from "../noc/CreditCardDisplay";
import axios from "axios";

const NotaryUserPaymentModal = ({
  open,
  onClose,
  notaryUserData,
  currentUser,
  currentSession,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const [paymentCards, setPaymentCards] = useState([]);
  const [cardActiveStep, setCardActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  // Notary search states
  const [searchingNotary, setSearchingNotary] = useState(false);
  const [foundNotary, setFoundNotary] = useState(null);
  const [notarySearched, setNotarySearched] = useState(false);

  const searchNotaryByName = useCallback(
    async (firstName, lastName, commissionNumber) => {
      setSearchingNotary(true);
      setErrorText("");

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/search-notary-by-name`,
          {
            first_name: firstName,
            last_name: lastName,
            commission_number: commissionNumber,
            currentSession: currentSession,
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          setFoundNotary(response.data.notary);
          setNotarySearched(true);

          // If commission is expired, show error
          if (response.data.notary.is_expired) {
            setErrorText(
              "Notary commission has expired. Please use a different name."
            );
          }
        } else {
          setFoundNotary(null);
          setNotarySearched(true);
          setErrorText(
            "Notary not found with the provided name and commission number."
          );
        }
      } catch (error) {
        setFoundNotary(null);
        setNotarySearched(true);
        setErrorText(
          error.response?.data?.message ||
            "Notary not found with the provided name and commission number."
        );
      } finally {
        setSearchingNotary(false);
      }
    },
    [currentSession]
  );

  useEffect(() => {
    if (open && currentUser?.payment_cards?.length > 0) {
      const cards = currentUser.payment_cards.map((card) => {
        const lastTwo = String(card.exp_year).slice(-2);
        const first = card.exp_month;

        return {
          id: card.id,
          cardNum: "**** **** **** " + card.last4,
          cardName: card.name ? card.name : card.card_holder,
          expires:
            (first < 10 ? "0" + String(first) : String(first)) + "/" + lastTwo,
          default: card.default,
        };
      });

      setPaymentCards(cards);
    }
  }, [open, currentUser?.payment_cards]);

  useEffect(() => {
    // Reset form when modal opens/closes
    if (open) {
      setErrorText("");
      setFoundNotary(null);
      setNotarySearched(false);

      // Search for notary automatically for both new users and conversions
      if (notaryUserData?.first && notaryUserData?.last) {
        searchNotaryByName(
          notaryUserData.first,
          notaryUserData.last,
          notaryUserData.commission_number || ""
        );
      } else if (open && notaryUserData) {
        // If names are missing, set error state
        setNotarySearched(true);
        setFoundNotary(null);
        setErrorText("User name information is missing. Please try again.");
      }
    }
  }, [open, notaryUserData, searchNotaryByName]);

  const handlePayment = async () => {
    if (paymentCards.length === 0) {
      setErrorText(
        "No payment cards available. Please add a payment card first."
      );
      return;
    }

    setLoading(true);
    setErrorText("");

    try {
      // First, make the Stripe payment for notary user
      const paymentResp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/pay-notary-user`,
        {
          paymentCard: paymentCards[cardActiveStep].id,
          currentSession: currentSession,
          notaryUserData: notaryUserData,
          is_main_user: notaryUserData?.isMainUser,
        },
        { withCredentials: true }
      );

      if (paymentResp.data.success) {
        if (notaryUserData.isConversion) {
          let payload = {
            subscription_id: paymentResp.data.subscription_id,
            price_id: paymentResp.data.price_id,
            currentSession: currentSession,
          };

          // Add all notary details from found notary
          if (foundNotary) {
            payload.notary_id = foundNotary.id;
            payload.first_name = foundNotary.firstName || "";
            payload.last_name = foundNotary.lastName || "";
            payload.middle_name = foundNotary.middleName || "";
            payload.birth_date = foundNotary.birthDate || "";
            payload.commission_number = foundNotary.commissionNumber;
            payload.commission_expiry = foundNotary.commissionExpiry;
            payload.bond_agency_id = foundNotary.bondAgencyId || "";
            payload.state = foundNotary.state;
            payload.expired = foundNotary.expired || 0;
            payload.notary_type = foundNotary.notaryType;
            payload.address = foundNotary.address || "";
            payload.city = foundNotary.city || "";
            payload.zip = foundNotary.zip || "";
            payload.zip_plus_4 = foundNotary.zipPlus4 || "";
            payload.county = foundNotary.county || "";
            payload.email = foundNotary.email || "";
            payload.phone = foundNotary.phone || "";
          }

          if (notaryUserData?.isMainUser) {
            payload.is_main_user = true;
          } else {
            payload.subuserId = notaryUserData.subuserId;
          }

          // Convert existing subuser to notary with commission details
          const conversionResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/subuser/convert-to-notary`,
            payload,
            { withCredentials: true }
          );

          if (conversionResp.data.success) {
            onPaymentSuccess(conversionResp.data.message);
            onClose();
          } else {
            onPaymentError(conversionResp.data.message);
          }
        } else {
          // Create new subuser with notary data
          const notaryUserPayload = {
            is_notary: true,
            subscription_id: paymentResp.data.subscription_id,
            price_id: paymentResp.data.price_id,
          };

          // If notary was found, include all notary details
          if (foundNotary) {
            notaryUserPayload.notary_id = foundNotary.id;
            notaryUserPayload.first_name = foundNotary.firstName || "";
            notaryUserPayload.last_name = foundNotary.lastName || "";
            notaryUserPayload.middle_name = foundNotary.middleName || "";
            notaryUserPayload.birth_date = foundNotary.birthDate || "";
            notaryUserPayload.commission_number = foundNotary.commissionNumber;
            notaryUserPayload.commission_expiry = foundNotary.commissionExpiry;
            notaryUserPayload.bond_agency_id = foundNotary.bondAgencyId || "";
            notaryUserPayload.state = foundNotary.state;
            notaryUserPayload.expired = foundNotary.expired || 0;
            notaryUserPayload.notary_type = foundNotary.notaryType;
            notaryUserPayload.address = foundNotary.address || "";
            notaryUserPayload.city = foundNotary.city || "";
            notaryUserPayload.zip = foundNotary.zip || "";
            notaryUserPayload.zip_plus_4 = foundNotary.zipPlus4 || "";
            notaryUserPayload.county = foundNotary.county || "";
            notaryUserPayload.email = foundNotary.email || "";
            notaryUserPayload.phone = foundNotary.phone || "";
          }

          const subuserResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/subuser/add-subuser`,
            {
              values: {
                ...notaryUserData,
                notary_user: notaryUserPayload,
              },
              currentSession: currentSession,
            },
            { withCredentials: true }
          );

          if (subuserResp.data.success) {
            onPaymentSuccess(subuserResp.data.message);
            onClose();
          } else {
            onPaymentError(subuserResp.data.message);
          }
        }
      } else {
        onPaymentError(paymentResp.data.message);
      }
    } catch (error) {
      onPaymentError(
        error.response?.data?.message || "Payment failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getNotaryPrice = () => {
    return currentUser?.tier?.title === "Premium" ? "149.00" : "99.00";
  };

  const getNotaryLimit = () => {
    // Admin users get 10 notary users
    if (currentUser?.user?.role === "admin") {
      return "10";
    }
    return currentUser?.tier?.title === "Premium" ? "2" : "Unlimited";
  };

  const getNotaryTypeLabel = (notaryType) => {
    switch (notaryType) {
      case 0:
        return "IPEN Only";
      case 1:
        return "RON Only";
      case 2:
        return "Both (IPEN & RON)";
      default:
        return "Unknown";
    }
  };

  return (
    <Modal open={open} sx={{ overflow: "auto" }}>
      <Box
        sx={{
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90%",
          maxWidth: "50rem",
          bgcolor: "transparent",
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Card
          sx={{
            maxWidth: "45rem",
            bgcolor: "background.paper",
            mx: "auto",
            my: "auto",
            border: "3px solid #d3d3d3",
            borderRadius: "18px",
            boxShadow: "2px 0px 10px 0px #d3d3d3",
            p: 3,
          }}
        >
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <Typography variant="h5" color="primary">
                {notaryUserData?.isConversion && !notaryUserData?.isMainUser
                  ? "Convert to Notary User"
                  : notaryUserData?.isMainUser
                    ? "Setup Notary User"
                    : "Notary User Payment"}
              </Typography>
              <IconButton onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" gutterBottom>
                  {notaryUserData?.isConversion && !notaryUserData?.isMainUser
                    ? `You are converting ${notaryUserData.first} ${notaryUserData.last} to a Notary User. This will incur an additional charge of $${getNotaryPrice()}/year.`
                    : notaryUserData?.isMainUser
                      ? `You are setting up ${notaryUserData.first} ${notaryUserData.last} as a Notary User. This will incur an additional charge of $${getNotaryPrice()}/year.`
                      : `You are adding a Notary User to your account. This will incur an additional charge of $${getNotaryPrice()}/year.`}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Notary User Limit: {getNotaryLimit()}
                </Typography>
              </Grid>

              {/* Combined User Details and Notary Search Results */}
              <Grid size={{ xs: 12 }}>
                {searchingNotary ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      Searching for notary...
                    </Typography>
                  </Box>
                ) : notarySearched && !foundNotary ? (
                  <Box
                    sx={{
                      p: 2,
                      border: "2px solid #ff9800",
                      borderRadius: 2,
                      bgcolor: "#fff3e0",
                    }}
                  >
                    <Typography variant="h6" color="error" gutterBottom>
                      ⚠️ Notary Not Found
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {errorText ||
                        `No notary record found for ${notaryUserData?.first} ${notaryUserData?.last}. Please verify the name and commission number, or try a different user.`}
                    </Typography>
                    {!notaryUserData?.isMainUser && (
                      <>
                        <Typography variant="body2">
                          <strong>Email:</strong>
                          {notaryUserData?.isMainUser
                            ? ` ${currentUser?.user?.email}`
                            : ` ${notaryUserData?.email}`}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Role:</strong>
                          {notaryUserData?.isMainUser
                            ? ` ${currentUser?.user?.role}`
                            : ` ${notaryUserData?.role}`}
                        </Typography>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      border: `2px solid ${
                        foundNotary?.is_expired
                          ? "#f44336"
                          : foundNotary
                            ? "#4caf50"
                            : "#2196f3"
                      }`,
                      borderRadius: 2,
                      bgcolor: foundNotary?.is_expired
                        ? "#ffebee"
                        : foundNotary
                          ? "#e8f5e9"
                          : "#e3f2fd",
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {foundNotary?.is_expired
                        ? "⚠️ Notary Found (Commission Expired)"
                        : foundNotary
                          ? "✓ User & Notary Details"
                          : "User Details"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.personal?.[0]?.firstName} ${currentUser?.user?.personal?.[0]?.lastName}`
                        : ` ${notaryUserData?.first} ${notaryUserData?.last}`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.email}`
                        : ` ${notaryUserData?.email}`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Role:</strong>
                      {notaryUserData?.isMainUser
                        ? ` ${currentUser?.user?.role}`
                        : ` ${notaryUserData?.role}`}
                    </Typography>

                    {foundNotary && (
                      <>
                        <Box
                          sx={{
                            borderTop: "1px solid",
                            borderColor: foundNotary.is_expired
                              ? "#f44336"
                              : "#4caf50",
                            mt: 2,
                            pt: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: "bold" }}
                          >
                            Notary Commission Details:
                          </Typography>
                          <Typography variant="body2">
                            <strong>Commission Number:</strong>{" "}
                            {foundNotary.commissionNumber}
                          </Typography>
                          <Typography variant="body2">
                            <strong>State:</strong> {foundNotary.state}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Commission Expiry:</strong>{" "}
                            {foundNotary.commissionExpiry || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Notary Type:</strong>{" "}
                            {getNotaryTypeLabel(foundNotary.notaryType)}
                          </Typography>
                          {foundNotary.middleName && (
                            <Typography variant="body2">
                              <strong>Middle Name:</strong>{" "}
                              {foundNotary.middleName}
                            </Typography>
                          )}
                          {foundNotary.address && (
                            <Typography variant="body2">
                              <strong>Address:</strong> {foundNotary.address}
                            </Typography>
                          )}
                          {foundNotary.city && (
                            <Typography variant="body2">
                              <strong>City:</strong> {foundNotary.city}
                            </Typography>
                          )}
                          {foundNotary.zip && (
                            <Typography variant="body2">
                              <strong>Zip:</strong> {foundNotary.zip}
                            </Typography>
                          )}
                          {foundNotary.county && (
                            <Typography variant="body2">
                              <strong>County:</strong> {foundNotary.county}
                            </Typography>
                          )}
                          {foundNotary.email && (
                            <Typography variant="body2">
                              <strong>Email:</strong> {foundNotary.email}
                            </Typography>
                          )}
                          {foundNotary.phone && (
                            <Typography variant="body2">
                              <strong>Phone:</strong> {foundNotary.phone}
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Payment Section - Always shown */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Select Payment Method:
                </Typography>
                {paymentCards.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: 3,
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                    }}
                  >
                    <Typography color="error" gutterBottom>
                      No payment cards available
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                    >
                      Please add a payment card first before proceeding with the
                      notary user payment.
                    </Typography>
                  </Box>
                ) : (
                  <CreditCardDisplay
                    cards={paymentCards}
                    activeStep={cardActiveStep}
                    setActiveStep={setCardActiveStep}
                    checkDefault={false}
                    loading={false}
                    setLoading={() => {}}
                  />
                )}
              </Grid>

              {errorText && (
                <Grid size={{ xs: 12 }}>
                  <Typography color="error" textAlign="center">
                    {errorText}
                  </Typography>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handlePayment}
                    disabled={
                      loading ||
                      paymentCards.length === 0 ||
                      !foundNotary ||
                      foundNotary?.is_expired
                    }
                    startIcon={loading && <CircularProgress size={20} />}
                  >
                    {loading
                      ? "Processing..."
                      : `Pay $${getNotaryPrice()}/year`}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Modal>
  );
};

export default NotaryUserPaymentModal;
